import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { connect } from "react-redux";
import { Helmet } from "react-helmet";
import { ToolCard } from "views/components/ToolCard";
import { Layout } from "views/common/Layout";
import { Loader2 } from "lucide-react";
import { searchTools } from "actions/tool";

const ToolSearch = (props) => {
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(true);
    const searchParams = new URLSearchParams(location.search);
    const query = searchParams.get("q") || "";

    useEffect(() => {
        if (query) {
            props.searchTools(query);
        }
        setIsLoading(false);
    }, [location.search]);

    if (isLoading) {
        return (
            <Layout>
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="flex justify-center items-center min-h-[50vh]">
                        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <Helmet>
                <title>
                    {query
                        ? `Search Results for "${query}" | Launch Hero FYI`
                        : "Search Tools | Launch Hero FYI"}
                </title>
                <meta
                    name="description"
                    content={
                        query
                            ? `Find the best AI tools matching "${query}". Browse through ${props.searchResults.length} curated tools and solutions.`
                            : "Search through our curated collection of AI tools and find exactly what you need."
                    }
                />
                <meta
                    name="keywords"
                    content={`${query}, AI tools, software search, tool finder, ${props.searchResults
                        .map((tool) => tool.tags)
                        .flat()
                        .join(", ")}`}
                />
                <meta
                    property="og:title"
                    content={
                        query
                            ? `Search Results for "${query}" | Launch Hero FYI`
                            : "Search Tools | Launch Hero FYI"
                    }
                />
                <meta
                    property="og:description"
                    content={
                        query
                            ? `Find the best AI tools matching "${query}". Browse through ${props.searchResults.length} curated tools and solutions.`
                            : "Search through our curated collection of AI tools and find exactly what you need."
                    }
                />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={window.location.href} />
                <meta name="twitter:card" content="summary" />
                <meta
                    name="twitter:title"
                    content={
                        query
                            ? `Search Results for "${query}" | Launch Hero FYI`
                            : "Search Tools | Launch Hero FYI"
                    }
                />
                <meta
                    name="twitter:description"
                    content={
                        query
                            ? `Find the best AI tools matching "${query}". Browse through ${props.searchResults.length} curated tools and solutions.`
                            : "Search through our curated collection of AI tools and find exactly what you need."
                    }
                />
                <link rel="canonical" href={window.location.href} />
            </Helmet>
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold tracking-tight">
                        Search Results
                    </h1>
                    <p className="mt-2 text-gray-600">
                        {props.searchResults.length} tools found
                    </p>
                </div>

                {props.searchResults.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {props.searchResults.map((tool) => (
                            <ToolCard
                                key={tool.id}
                                id={tool.id}
                                image={tool.image}
                                title={tool.title}
                                description={tool.description}
                                url={tool.url}
                                loves={tool.loves}
                                tags={tool.tags}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-12">
                        <h2 className="text-xl font-semibold text-gray-900">
                            No tools found
                        </h2>
                        <p className="mt-2 text-gray-600">
                            Try different search terms or browse all tools
                        </p>
                    </div>
                )}
            </div>
        </Layout>
    );
};

const mapStateToProps = (state) => ({
    searchResults: state.tool.searchResults,
});

export default connect(mapStateToProps, { searchTools })(ToolSearch);
